import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Briefcase, GraduationCap, Cpu, BrainCircuit,
  TrendingUp, Award, Building2, ShieldCheck, Globe2, Users, Target, Mail, MapPin,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Reveal, SectionHeading, Marquee, CourseCard, ExpertCard, InsightCard, VideoCard, EASE } from "@/components/ui-bits";
import VideoModal from "@/components/VideoModal";

const SERVICE_ICONS = [Briefcase, GraduationCap, Cpu, BrainCircuit, TrendingUp, Award, Building2];
const WHY_ICONS = [ShieldCheck, Globe2, Users, Target];

export default function Home() {
  const { t, lang, isRTL } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [experts, setExperts] = useState([]);
  const [insights, setInsights] = useState([]);
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    api.get("/courses", { params: { featured: true } }).then((r) => setCourses(r.data.slice(0, 3))).catch(() => {});
    api.get("/experts", { params: { featured: true } }).then((r) => setExperts(r.data.slice(0, 3))).catch(() => {});
    api.get("/insights", { params: { featured: true } }).then((r) => setInsights(r.data.slice(0, 3))).catch(() => {});
    api.get("/videos", { params: { featured: true } }).then((r) => setVideos(r.data.slice(0, 2))).catch(() => {});
  }, []);

  const stats = [
    ["250+", t("hero.stat1")],
    ["15+", t("hero.stat2")],
    ["98%", t("hero.stat3")],
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="hero-grain relative overflow-hidden bg-navy-950">
        <motion.img
          src={IMAGES.hero}
          alt="London skyline"
          style={{ y: bgY }}
          className="absolute inset-0 h-[120%] w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/40 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-24 sm:px-6 sm:pt-32 lg:px-12 lg:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 text-xs font-bold uppercase tracking-[0.3em] text-gold"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.p>
          <h1
            key={lang}
            data-testid="hero-headline"
            className="max-w-4xl font-display text-5xl font-semibold leading-[1.08] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            {[t("hero.line1"), t("hero.line2"), t("hero.line3")].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-gold" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, ease: EASE, delay: 0.15 + i * 0.14 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.7 }}
            className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheadline"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/consultation"
              data-testid="hero-book-consultation-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
            >
              {t("hero.cta1")}
              <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-explore-services-cta"
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold-light"
            >
              {t("hero.cta2")}
            </Link>
            <a
              href="https://stratedge-business-diagnostic.lovable.app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-business-diagnostic-cta"
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold hover:text-gold-light"
            >
              {t("hero.ctaDiagnostic")}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-16 grid max-w-3xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            {stats.map(([value, label], i) => (
              <div key={i} data-testid={`hero-stat-${i}`}>
                <p className="font-display text-3xl font-semibold text-gold sm:text-4xl">{value}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400 sm:text-xs">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* INTRO */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading eyebrow={t("intro.eyebrow")} title={t("intro.title")} />
            <Reveal delay={0.1}>
              <p className="-mt-6 text-base leading-relaxed text-slate-600">{t("intro.body")}</p>
              <ul className="mt-8 space-y-4">
                {[t("intro.point1"), t("intro.point2"), t("intro.point3")].map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="mt-1 h-2 w-2 shrink-0 rotate-45 bg-gold" />
                    {point}
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                data-testid="intro-about-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-hover hover:text-navy"
              >
                {t("nav.about")} <Arrow className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="overflow-hidden rounded-lg shadow-2xl">
              <img src={IMAGES.executive} alt="Stratedge UK consulting" className="h-[420px] w-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-8 -start-4 hidden w-56 overflow-hidden rounded-lg border-4 border-white shadow-xl sm:block">
              <img src={IMAGES.workshop} alt="Executive training" className="h-40 w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHeading eyebrow={t("services.eyebrow")} title={t("services.title")} sub={t("services.sub")} />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {SERVICE_ICONS.map((Icon, i) => (
              <Reveal
                key={i}
                delay={i * 0.06}
                className={i === 0 ? "md:col-span-2 lg:row-span-2" : ""}
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`home-service-card-${i + 1}`}
                  className={`group flex h-full flex-col justify-between rounded-lg border border-slate-200 bg-white p-7 shadow-sm transition-colors hover:border-gold/60 ${
                    i === 0 ? "bg-navy text-white" : ""
                  }`}
                  style={i === 0 ? { backgroundColor: "#0B132B" } : {}}
                >
                  <div>
                    <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${i === 0 ? "bg-gold/15 text-gold" : "bg-gold-soft text-gold-hover"}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className={`mt-5 font-display text-2xl font-semibold ${i === 0 ? "text-white" : "text-navy"}`}>
                      {t(`s${i + 1}.title`)}
                    </h3>
                    <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-slate-300" : "text-slate-600"}`}>
                      {t(`s${i + 1}.desc`)}
                    </p>
                  </div>
                  <Link
                    to="/services"
                    className={`mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${i === 0 ? "text-gold" : "text-gold-hover"}`}
                  >
                    {t("services.viewAll")} <Arrow className="h-3.5 w-3.5" />
                  </Link>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY STRATEDGE */}
      <section className="hero-grain relative overflow-hidden bg-navy py-20 lg:py-28">
        <img src={IMAGES.cityNight} alt="" className="absolute inset-0 h-full w-full object-cover opacity-10" loading="lazy" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHeading dark eyebrow={t("why.eyebrow")} title={t("why.title")} />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_ICONS.map((Icon, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`why-card-${i + 1}`} className="h-full rounded-lg border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors hover:border-gold/40">
                  <Icon className="h-7 w-7 text-gold" />
                  <h3 className="mt-4 font-display text-xl font-semibold text-white">{t(`why.${i + 1}.title`)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`why.${i + 1}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-16">
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.24em] text-gold">{t("expertise.eyebrow")}</p>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <span
                  key={i}
                  data-testid={`expertise-chip-${i}`}
                  className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-gold hover:text-gold-light"
                >
                  {t(`expertise.${i}`)}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow={t("training.eyebrow")} title={t("training.title")} sub={t("training.sub")} />
            <Reveal className="mb-12 flex gap-3">
              <Link to="/training" data-testid="home-browse-courses-cta" className="rounded-full bg-navy px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-800">
                {t("training.viewAll")}
              </Link>
              <Link to="/training?request=1" data-testid="home-request-course-cta" className="rounded-full border border-gold px-6 py-3 text-xs font-bold uppercase tracking-wider text-gold-hover transition-colors hover:bg-gold-soft">
                {t("training.requestCta")}
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <Reveal key={c.id}><CourseCard course={c} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTS TEASER */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow={t("experts.eyebrow")} title={t("experts.title")} sub={t("experts.sub")} />
            <Reveal className="mb-12">
              <Link to="/experts" data-testid="home-meet-experts-cta" className="rounded-full bg-navy px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-navy-800">
                {t("experts.viewAll")}
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {experts.map((e) => (
              <Reveal key={e.id}><ExpertCard expert={e} /></Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-16 overflow-hidden rounded-lg bg-navy">
            <div className="grid items-center gap-8 p-8 sm:p-12 lg:grid-cols-[1fr_auto]">
              <div>
                <h3 className="font-display text-3xl font-semibold text-white">{t("experts.joinCta")}</h3>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300">{t("experts.joinSub")}</p>
              </div>
              <Link
                to="/join"
                data-testid="home-join-network-cta"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gold px-8 py-4 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
              >
                {t("experts.joinCta")} <Arrow className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSIGHTS + VIDEOS */}
      {(insights.length > 0 || videos.length > 0) && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading eyebrow={t("insights.eyebrow")} title={t("insights.title")} />
              <Reveal className="mb-12 flex gap-3">
                <Link to="/insights" data-testid="home-all-insights-cta" className="rounded-full border border-navy px-6 py-3 text-xs font-bold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white">
                  {t("insights.viewAll")}
                </Link>
                <Link to="/videos" data-testid="home-all-videos-cta" className="rounded-full border border-gold px-6 py-3 text-xs font-bold uppercase tracking-wider text-gold-hover transition-colors hover:bg-gold-soft">
                  {t("videos.viewAll")}
                </Link>
              </Reveal>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {insights.map((a) => (
                <Reveal key={a.id}><InsightCard article={a} /></Reveal>
              ))}
            </div>
            {videos.length > 0 && (
              <div className="mt-10 grid gap-6 md:grid-cols-2">
                {videos.map((v) => (
                  <Reveal key={v.id}><VideoCard video={v} onPlay={setActiveVideo} /></Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-20 lg:py-24">
        <div className="gold-hairline absolute top-0 w-full" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-display text-4xl font-semibold text-white sm:text-5xl" data-testid="cta-title">{t("cta.title")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">{t("cta.sub")}</p>
            <Link
              to="/consultation"
              data-testid="cta-book-consultation-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
            >
              {t("cta.button")} <Arrow className="h-4 w-4" />
            </Link>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
              <a href="mailto:info@stratedgeuk.com" data-testid="cta-email" className="inline-flex items-center gap-2 hover:text-gold-light">
                <Mail className="h-4 w-4 text-gold" /> info@stratedgeuk.com
              </a>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gold" /> {t("contact.hq")}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}
